import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, MapPin, Star } from "lucide-react";
import api from "@/api";
import ChapterHeading from "@/components/home/ChapterHeading";

export default function FoodSection({ cityId }) {
  const [spots, setSpots] = useState([]);

  useEffect(() => {
    api.get(`/food-spots?city=${cityId}`).then(({ data }) => setSpots(data)).catch(() => {});
  }, [cityId]);

  return (
    <section id="food" className="border-y border-white/10 bg-[#0E1117] py-24" data-testid="food-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <ChapterHeading
          num="04 // CULINARY & TASTE"
          title="Eat Like a Local"
          accent="text-neon-magenta"
          sub="Steak counters, speakeasies and 4AM taco windows. The places Philadelphians actually go when the bars let out."
        />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {spots.map((s, i) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="group overflow-hidden rounded-2xl border border-white/10 bg-[#131722] transition-all hover:-translate-y-1.5 hover:border-neon-magenta/40 hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
              data-testid={`food-card-${s.id}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131722] via-transparent to-transparent" />
                <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-md">
                  {s.style}
                </span>
                <span className="absolute right-4 top-4 flex items-center gap-1 rounded-full border border-neon-amber/40 bg-black/60 px-3 py-1 font-mono text-[10px] text-neon-amber backdrop-blur-md">
                  <Star className="h-3 w-3" fill="currentColor" />{s.rating}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight">{s.name}</h3>
                  <span className="font-mono text-xs text-neon-lime">{s.price_tier}</span>
                </div>
                <p className="mt-2 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                  <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{s.neighborhood}</span>
                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{s.hours}</span>
                </p>
                <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-zinc-400">{s.description}</p>
                <div className="mt-4 rounded-lg border border-neon-magenta/25 bg-neon-magenta/5 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-neon-magenta">
                  Order: {s.featured_dish}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
